/* Wild Idol cart drawer enhancements (Flow theme).
 *
 * Layered on top of the legacy assets/ajax-cart.js without modifying it.
 *
 *  - Registers a Handlebars helper `shortName` that strips the
 *    "Wild Idol Non-Alcoholic " brand prefix from product titles
 *    (case-insensitive), so cart-drawer rows read like a receipt.
 *  - Listens to `afterCartLoad.ajaxCart` (jQuery event the legacy plugin
 *    emits after every cart re-render) and updates:
 *      .wi-cart-shipping (progress bar fill, label, complete state, hidden)
 *      [data-wi-cart-count] (heading "(N)" pill)
 *  - Falls back to /cart.js fetch if the event payload is missing.
 *  - Re-renders the upsell strip server-side via Handlebars expansion,
 *    so no JS work needed for the upsell block.
 *  - Injects a quick-add product scroller when the cart is empty so
 *    the drawer isn't a dead end.
 */

// Quick-add product data injected at page-render time from
// layout/theme.liquid via <script id="WiQuickAddData" type="application/json">.
// See the snippet in theme.liquid for the data shape.
var WI_QUICK_ADD_PRODUCTS = (function () {
  var el = document.getElementById('WiQuickAddData');
  if (!el) return [];
  try { return JSON.parse(el.textContent); } catch (e) { return []; }
})();

(function () {
  'use strict';

  var FREE_SHIP_THRESHOLD_CENTS = 7000; // Shopify Domestic zone: Free Shipping at >= $70 (checked 2026-09-08)

  var BRAND_PREFIX_RE = /^\s*Wild\s+Idol\s+Non[‐‑‒–—―-]?Alcoholic\s+/i;

  function stripBrand(name) {
    if (!name) return '';
    return String(name).replace(BRAND_PREFIX_RE, '').trim();
  }

  // Register the Handlebars helper as soon as the global is available.
  function registerHelper() {
    if (typeof window.Handlebars !== 'undefined' && !window.Handlebars._wiShortNameRegistered) {
      window.Handlebars.registerHelper('shortName', function (val) {
        return stripBrand(val);
      });
      window.Handlebars._wiShortNameRegistered = true;
    }
  }

  // The legacy ajax-cart.js calls Handlebars.compile after this file runs
  // (both are <script defer>, but handlebars.min.js comes first). We poll
  // for Handlebars up to 5s just in case.
  registerHelper();
  if (!window.Handlebars || !window.Handlebars._wiShortNameRegistered) {
    var tries = 0;
    var iv = setInterval(function () {
      registerHelper();
      tries++;
      if (window.Handlebars && window.Handlebars._wiShortNameRegistered) {
        clearInterval(iv);
      } else if (tries > 50) {
        clearInterval(iv);
      }
    }, 100);
  }

  // ---------- Shipping bar / count updaters ----------
  function setText(el, text) { if (el) el.textContent = text; }

  function formatMoneyUSD(cents) {
    var dollars = (cents / 100);
    return '$' + dollars.toFixed(2);
  }

  function updateShippingBar(cart) {
    var bar = document.querySelector('[data-wi-cart-shipping]');
    if (!bar) return;
    var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
    var itemCount = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
    var remaining = Math.max(0, FREE_SHIP_THRESHOLD_CENTS - total);
    var pct = Math.min(100, Math.round((total / FREE_SHIP_THRESHOLD_CENTS) * 100));
    if (itemCount === 0) pct = 0;

    var fill = bar.querySelector('[data-wi-shipping-fill]');
    if (fill) fill.style.width = pct + '%';

    var track = bar.querySelector('[role="progressbar"]');
    if (track) track.setAttribute('aria-valuenow', String(pct));

    var label = bar.querySelector('[data-wi-shipping-label]');
    if (label) {
      if (remaining === 0 && itemCount > 0) {
        label.textContent = 'Free Shipping Unlocked';
      } else {
        label.textContent = 'Add ' + formatMoneyUSD(remaining) + ' more for Free Shipping';
      }
    }

    /* 2026-09-09: hide the bar on an empty cart AND on a single-bottle cart
       that has not reached the threshold. With shipping at $5, "Add $20.01
       more for Free Shipping" asks the shopper to spend $20 to save $5, and on
       mobile it sits directly above Checkout. The single bottle is the entry
       purchase we most want to complete, so the drawer stays quiet there.

       A one-line cart that HAS cleared the threshold still shows the bar: the
       Twin Pack is a single line item at $89.99, and it is the cart the upsell
       just created, so the shopper should see free shipping confirmed rather
       than nothing. Keep this in step with snippets/cart-shipping-bar.liquid. */
    var hide = itemCount === 0 || (itemCount === 1 && remaining > 0);
    bar.classList.toggle('wi-cart-shipping--complete', itemCount > 0 && remaining === 0);
    bar.classList.toggle('wi-cart-shipping--hidden', hide);
    bar.hidden = hide;
  }

  /* Shipping cost in the drawer footer (2026-09-09).
     Shipping is a flat $5 under the $70 threshold and free at or above it, so
     the footer states the real figure instead of "calculated at checkout".
     Learning the cost only on the checkout page is a price rise after the
     shopper has committed, and that is where this cart loses people. */
  function updateShippingLine(cart) {
    var el = document.querySelector('[data-wi-shipping-cost]');
    if (!el) return;
    var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
    var itemCount = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
    var shipping = 0;
    if (itemCount === 0) {
      el.textContent = 'calculated at checkout';
    } else if (total >= FREE_SHIP_THRESHOLD_CENTS) {
      el.textContent = 'Free';
    } else {
      shipping = WI_SINGLE_SHIPPING_CENTS;
      el.textContent = formatMoneyUSD(shipping) + ' flat rate';
    }

    /* 2026-09-11: state the delivered total (before tax) in the drawer so the
       number the shopper sees at checkout is the one they already agreed to.
       Six of this week's real checkout starts left the first checkout screen
       within seconds, where shipping is hidden until an address is typed. */
    var totalRow = document.querySelector('[data-wi-est-total-row]');
    var totalEl = document.querySelector('[data-wi-est-total]');
    if (totalRow && totalEl) {
      if (itemCount > 0) {
        totalEl.textContent = formatMoneyUSD(total + shipping);
        totalRow.hidden = false;
      } else {
        totalRow.hidden = true;
      }
    }
  }

  function updateCount(cart) {
    var pill = document.querySelector('[data-wi-cart-count]');
    var title = document.getElementById('CartTitle');
    if (!title) return;
    var count = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
    if (count === 0) {
      if (pill) pill.remove();
      return;
    }
    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'wi-cart-count';
      pill.setAttribute('data-wi-cart-count', '');
      title.appendChild(document.createTextNode(' '));
      title.appendChild(pill);
    }
    pill.textContent = '(' + count + ')';
  }

  // ---- Empty-cart quick-add scroller ----

  function buildQuickAddTile(product) {
    var tile = document.createElement('div');
    tile.className = 'wi-cart-qa__tile';

    var imgWrap = document.createElement('a');
    imgWrap.href = product.url;
    imgWrap.className = 'wi-cart-qa__img-wrap';
    imgWrap.tabIndex = -1;
    imgWrap.setAttribute('aria-hidden', 'true');

    var img = document.createElement('img');
    img.src = product.image;
    img.alt = product.title;
    img.loading = 'lazy';
    img.width = 100;
    img.height = 100;
    imgWrap.appendChild(img);

    var info = document.createElement('div');
    info.className = 'wi-cart-qa__info';

    var name = document.createElement('a');
    name.href = product.url;
    name.className = 'wi-cart-qa__name';
    // Force the title onto two lines for consistent tile heights across
    // products with different word counts ("Sparkling Rosé" is 2 words,
    // "Gift Box Twin Pack" is 4). Split on the first space; if a title
    // has no space, leave it as a single line.
    var firstSpace = product.title.indexOf(' ');
    if (firstSpace > 0) {
      var firstWord = product.title.slice(0, firstSpace);
      var rest = product.title.slice(firstSpace + 1);
      name.innerHTML =
        firstWord +
        '<br>' +
        rest.replace(/[<>&]/g, function (c) {
          return c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;';
        });
    } else {
      name.textContent = product.title;
    }

    var price = document.createElement('span');
    price.className = 'wi-cart-qa__price';
    price.textContent = product.price;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'wi-cart-qa__add';
    btn.setAttribute('data-variant-id', product.id);
    btn.setAttribute('aria-label', 'Add ' + product.title + ' to cart');
    btn.textContent = product.available ? '+ Add' : 'Sold out';
    btn.disabled = !product.available;

    btn.addEventListener('click', function () {
      btn.disabled = true;
      btn.textContent = '…';
      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: product.id, quantity: 1 })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          // Trigger the legacy ajax-cart.js to re-render the drawer.
          // ajaxCart.load() is the public API exposed by ajax-cart.js.liquid
          // (module.load = loadCart). This does getCart(cartUpdateCallback)
          // which rebuilds #CartContainer and fires afterCartLoad.ajaxCart.
          if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
            window.ajaxCart.load();
          } else if (window.ShopifyAPI && typeof window.ShopifyAPI.getCart === 'function') {
            window.ShopifyAPI.getCart(function () {});
          } else {
            window.location.reload();
          }
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = '+ Add';
        });
    });

    info.appendChild(name);
    info.appendChild(price);
    info.appendChild(btn);

    tile.appendChild(imgWrap);
    tile.appendChild(info);
    return tile;
  }

  function injectEmptyCartScroller() {
    var container = document.getElementById('CartContainer');
    if (!container) return;
    // Only inject when the empty-cart state is actually rendered.
    var emptyMsg = container.querySelector('.empty-cart');
    if (!emptyMsg) return;
    // Don't double-inject.
    if (container.querySelector('.wi-cart-qa')) return;
    // Don't inject if no products are available.
    if (!WI_QUICK_ADD_PRODUCTS || WI_QUICK_ADD_PRODUCTS.length === 0) return;

    var section = document.createElement('div');
    section.className = 'wi-cart-qa';

    var heading = document.createElement('p');
    heading.className = 'wi-cart-qa__heading';
    heading.textContent = 'Popular picks';
    section.appendChild(heading);

    var strip = document.createElement('div');
    strip.className = 'wi-cart-qa__strip';
    WI_QUICK_ADD_PRODUCTS.forEach(function (p) {
      strip.appendChild(buildQuickAddTile(p));
    });
    section.appendChild(strip);

    // Insert after the empty message.
    emptyMsg.insertAdjacentElement('afterend', section);
  }

  // ---- Twin Pack upsell (snippets/wi-cart-upsell.liquid) ----
  //
  // The drawer bakes the upsell markup into its Handlebars template at page
  // load, so its numbers and visibility are corrected here after every cart
  // change. Shown only when the cart holds exactly one single bottle of Rosé
  // or White and the Twin Pack is not already in it.
  var WI_SINGLE_SHIPPING_CENTS = 500; // Standard Shipping under the free threshold (Shopify Domestic zone; $5 test from 2026-09-09)

  function wiTwinState(cart, card) {
    var twinId = parseInt(card.getAttribute('data-twin-variant'), 10);
    var roseHandle = card.getAttribute('data-rose-handle');
    var whiteHandle = card.getAttribute('data-white-handle');
    var items = (cart && cart.items) || [];
    var twinInCart = items.some(function (i) { return i.variant_id === twinId; });
    if (twinInCart || cart.item_count !== 1 || items.length !== 1) return null;
    var single = items[0];
    if (single.handle !== roseHandle && single.handle !== whiteHandle) return null;
    return { single: single, isWhite: single.handle === whiteHandle, twinId: twinId };
  }

  function updateTwinUpsell(cart) {
    var cards = document.querySelectorAll('[data-wi-twin-upsell]');
    if (!cards.length) return;
    Array.prototype.forEach.call(cards, function (card) {
      var state = wiTwinState(cart, card);
      if (!state) {
        card.hidden = true;
        card.classList.add('wi-twin-upsell--hidden');
        return;
      }
      var twinPrice = parseInt(card.getAttribute('data-twin-price'), 10);
      var delta = twinPrice - (state.single.final_line_price + WI_SINGLE_SHIPPING_CENTS);
      var deltaEl = card.querySelector('[data-wi-twin-delta]');
      if (deltaEl) {
        deltaEl.textContent = delta > 0
          ? 'A second bottle for ' + formatMoneyUSD(delta) + ' more than one bottle plus shipping.'
          : '';
      }
      var alt = card.querySelector('[data-wi-twin-addsame]');
      if (alt) alt.textContent = state.isWhite ? 'Add another White instead' : 'Add another Rosé instead';
      card.hidden = false;
      card.classList.remove('wi-twin-upsell--hidden');
    });
  }

  function reloadDrawer() {
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      window.ajaxCart.load();
    } else if (window.ShopifyAPI && typeof window.ShopifyAPI.getCart === 'function') {
      window.ShopifyAPI.getCart(function () {});
    } else {
      window.location.reload();
    }
  }

  function cartPost(path, body) {
    return window.fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(path + ' ' + r.status);
      return r.json();
    });
  }

  function twinAction(btn, mode) {
    var card = btn.closest('[data-wi-twin-upsell]');
    if (!card || typeof window.fetch !== 'function') return;
    var buttons = card.querySelectorAll('button');
    Array.prototype.forEach.call(buttons, function (b) { b.disabled = true; });
    var original = btn.textContent;
    btn.textContent = mode === 'swap' ? 'Upgrading…' : 'Adding…';
    window.fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var state = wiTwinState(cart, card);
        if (!state) { reloadDrawer(); return; }
        if (mode === 'swap') {
          // Replace the single bottle line with the Twin Pack.
          return cartPost('/cart/change.js', { id: state.single.key, quantity: 0 })
            .then(function () { return cartPost('/cart/add.js', { id: state.twinId, quantity: 1 }); })
            .then(reloadDrawer);
        }
        // Second bottle of the same wine.
        return cartPost('/cart/add.js', { id: state.single.variant_id, quantity: 1 }).then(reloadDrawer);
      })
      .catch(function () {
        Array.prototype.forEach.call(buttons, function (b) { b.disabled = false; });
        btn.textContent = original;
      });
  }

  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || typeof target.closest !== 'function') return;
    var swap = target.closest('[data-wi-twin-swap]');
    if (swap) { evt.preventDefault(); twinAction(swap, 'swap'); return; }
    var same = target.closest('[data-wi-twin-addsame]');
    if (same) { evt.preventDefault(); twinAction(same, 'same'); }
  });

  function applyCart(cart) {
    if (!cart) return;
    updateShippingBar(cart);
    updateShippingLine(cart);
    updateCount(cart);
    updateTwinUpsell(cart);
    if (cart.item_count === 0) {
      // Inject with a brief timeout to allow ajax-cart.js to finish
      // populating #CartContainer with the empty-cart message first.
      setTimeout(injectEmptyCartScroller, 50);
    }
  }

  function fetchCartAndApply() {
    if (typeof window.fetch !== 'function') return;
    window.fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(applyCart)
      .catch(function () { /* swallow */ });
  }

  // Listen on document.body via jQuery if present, otherwise plain DOM.
  function bind() {
    if (window.jQuery) {
      window.jQuery(document.body).on('afterCartLoad.ajaxCart', function (evt, cart) {
        if (cart) applyCart(cart); else fetchCartAndApply();
      });
      window.jQuery(document.body).on('afterChangeItem.ajaxCart', function () {
        // ajax-cart.js follows changeItem with a getCart -> buildCart,
        // which triggers afterCartLoad. No-op here but keeps a hook.
      });
    }
    // Also refresh once on DOM ready in case the page loaded with items
    // already in the cart but no afterCartLoad has fired yet.
    fetchCartAndApply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  // Expose for debugging
  window.WICart = {
    stripBrand: stripBrand,
    updateShippingBar: updateShippingBar,
    updateCount: updateCount
  };
})();
